import {
  detectPlaceholders,
  fillTemplate,
  sanitizeTemplateName,
  type Template,
  type TemplateVariable,
  validateTemplate,
} from "@wcp/shared";
import { useEffect, useMemo, useState } from "react";
import { ApiError } from "../../lib/api.js";
import { useBusiness } from "../../lib/business.js";
import { uploadMediaFile } from "../../lib/upload.js";
import { useMe } from "../auth/api.js";
import { useCreateTemplate, useTemplates } from "./api.js";
import styles from "./TemplatesScreen.module.css";

type StatusFilter = "all" | "approved" | "pending" | "rejected";
type TypeFilter = "all" | "marketing" | "utility";

export function TemplatesScreen() {
  const templates = useTemplates();
  const me = useMe();
  const canCreate = me.data?.role === "admin" || me.data?.role === "maintainer";
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [type, setType] = useState<TypeFilter>("all");
  const [modalOpen, setModalOpen] = useState(false);

  const list = templates.data ?? [];
  const counts = {
    all: list.length,
    approved: list.filter((t) => t.status === "approved").length,
    pending: list.filter((t) => t.status === "pending").length,
    rejected: list.filter((t) => t.status === "rejected").length,
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((t) => {
      if (status !== "all" && t.status !== status) return false;
      if (type !== "all" && t.category !== type) return false;
      if (needle && !`${t.name} ${t.body}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [list, q, status, type]);

  return (
    <section className={styles.wrap}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Templates</h1>
          <p className={styles.sub}>Submitted &amp; checked via the WhatsApp Cloud API</p>
        </div>
        {canCreate && (
          <button type="button" className="btn btn--primary" onClick={() => setModalOpen(true)}>
            + New template
          </button>
        )}
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchbox}>
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.6" />
            <path d="M14 14l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input placeholder="Search templates…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && (
            <button
              type="button"
              className={styles.searchClear}
              aria-label="Clear search"
              onClick={() => setQ("")}
            >
              ✕
            </button>
          )}
        </div>
        <div className={styles.chips}>
          <div className={styles.fset}>
            {(["all", "approved", "pending", "rejected"] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                className={styles.chip}
                data-active={status === s}
                onClick={() => setStatus(s)}
              >
                {s === "all" ? "All" : s[0]?.toUpperCase() + s.slice(1)}{" "}
                <span className={styles.ct}>{counts[s]}</span>
              </button>
            ))}
          </div>
          <span className={styles.divider} />
          <div className={styles.fset}>
            {(["all", "marketing", "utility"] as TypeFilter[]).map((t) => (
              <button
                key={t}
                type="button"
                className={styles.chip}
                data-active={type === t}
                onClick={() => setType(t)}
              >
                {t === "all" ? "All types" : t[0]?.toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {templates.isLoading ? (
        <p className={styles.empty}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p className={styles.empty}>No templates match these filters.</p>
      ) : (
        <div className={styles.grid}>
          {filtered.map((t) => (
            <TemplateCard key={t.id} t={t} />
          ))}
        </div>
      )}

      {modalOpen && <NewTemplateModal onClose={() => setModalOpen(false)} />}
    </section>
  );
}

function StatusTag({ status }: { status: string }) {
  const kind =
    status === "approved"
      ? "ok"
      : status === "pending"
        ? "warn"
        : status === "rejected"
          ? "bad"
          : "muted";
  return (
    <span className={styles.tag} data-kind={kind}>
      {status[0]?.toUpperCase()}
      {status.slice(1)}
    </span>
  );
}

function TemplateCard({ t }: { t: Template }) {
  const [showVars, setShowVars] = useState(false);
  const preview = fillTemplate(t.body, t.variables);
  const steps = ["draft", "pending", "approved"];
  const reached = t.status === "rejected" ? 1 : steps.indexOf(t.status);
  return (
    <article className={`card ${styles.cardBox}`} data-status={t.status}>
      <div className={styles.cardHead}>
        <strong className={styles.cardName}>{t.displayName ?? t.name}</strong>
        <StatusTag status={t.status} />
      </div>
      <div className={styles.metaRow}>
        <span className={styles.catPill} data-cat={t.category}>
          {t.category}
        </span>
        <span className="t-mono">{t.language.toUpperCase()}</span>
        {t.status === "pending" && <span className={styles.checking}>● Checking with Meta…</span>}
      </div>
      <div className={styles.preview}>
        <div className={styles.waBubble}>
          {t.headerFormat === "image" && (
            <span className={styles.waHeaderImg}>
              {t.defaultHeaderMediaId != null ? (
                <img src={`/api/v1/media/${t.defaultHeaderMediaId}`} alt="Default header" />
              ) : (
                <span>Header image · set per broadcast</span>
              )}
            </span>
          )}
          <span className={styles.waText}>{preview}</span>
          <span className={styles.waTime}>
            <svg viewBox="0 0 18 12" fill="none" aria-hidden="true">
              <path
                d="M1 6.5l3 3 6.5-8M6.5 9.5l3-3.2M9.5 9.5l6.5-8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            10:24
          </span>
        </div>
      </div>
      {t.variables.length > 0 && (
        <div>
          <button type="button" className={styles.varToggle} onClick={() => setShowVars((v) => !v)}>
            {showVars ? "Hide" : "Show"} {t.variables.length} variable
            {t.variables.length > 1 ? "s" : ""}
            <span className={styles.caret} data-up={showVars}>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          {showVars && (
            <ul className={styles.varList}>
              {t.variables.map((v) => (
                <li key={v.index} className="t-mono">
                  {`{{${v.index}}}`} → {v.label || "Set on send"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className={styles.timeline} data-rejected={t.status === "rejected"}>
        {["Draft", "Pending", t.status === "rejected" ? "Rejected" : "Approved"].map((label, i) => (
          <span
            key={label}
            className={styles.tlStep}
            data-on={i <= reached}
            data-bad={t.status === "rejected" && i === 1}
          >
            <span className={styles.tlDot} />
            <span className={styles.tlLbl}>{label}</span>
          </span>
        ))}
      </div>
      {t.status === "rejected" && t.rejectedReason && (
        <p className={styles.reason}>{t.rejectedReason}</p>
      )}
    </article>
  );
}

function NewTemplateModal({ onClose }: { onClose: () => void }) {
  const create = useCreateTemplate();
  const { activeId } = useBusiness();
  const [name, setName] = useState("");
  const [category, setCategory] = useState<"utility" | "marketing" | "authentication">("utility");
  const [language, setLanguage] = useState<"ms" | "en">("ms");
  const [body, setBody] = useState("");
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [headerFormat, setHeaderFormat] = useState<"none" | "image">("none");
  const [headerFile, setHeaderFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  // Live preview URL for the chosen header image (revoked on change/unmount).
  const headerUrl = useMemo(
    () => (headerFile ? URL.createObjectURL(headerFile) : null),
    [headerFile],
  );
  useEffect(() => () => void (headerUrl && URL.revokeObjectURL(headerUrl)), [headerUrl]);

  const placeholders = detectPlaceholders(body);
  const cleanName = sanitizeTemplateName(name);
  const errors = validateTemplate({ name, body });
  const needsImage = headerFormat === "image" && !headerFile;
  const canSubmit = errors.length === 0 && !needsImage && !submitting && !create.isPending;

  const variables: TemplateVariable[] = placeholders.map((p, i) => ({
    index: Number(p) || i + 1,
    label: labels[p] ?? "",
  }));
  const previewText = body.trim() ? fillTemplate(body, variables) : "Your message appears here…";

  function pickImage(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setServerErrors(["Header must be an image (JPEG or PNG)."]);
      return;
    }
    setServerErrors([]);
    setHeaderFile(file);
  }

  async function submit() {
    setServerErrors([]);
    setSubmitting(true);
    try {
      let headerSampleMediaId: number | undefined;
      if (headerFormat === "image" && headerFile) {
        headerSampleMediaId = (await uploadMediaFile(activeId, headerFile)).id;
      }
      await create.mutateAsync({
        name: cleanName,
        category,
        language,
        body,
        variables,
        headerFormat,
        ...(headerSampleMediaId !== undefined ? { headerSampleMediaId } : {}),
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const details = (err as ApiError & { details?: string[] }).details;
        setServerErrors(Array.isArray(details) ? details : [err.message]);
      } else {
        setServerErrors([err instanceof Error ? err.message : "Submission failed."]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-close
    <div className={styles.scrim} onMouseDown={onClose}>
      <div
        className={`card ${styles.modal} ${styles.modalWide}`}
        role="dialog"
        aria-modal="true"
        aria-label="New template"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className={styles.modalTitle}>New template</h2>
        <p className={styles.modalNote}>
          Submitted to Meta for approval; status returns Pending → Approved / Rejected. Wrap
          anything personalised in {"{{1}}"} variables.
        </p>

        <div className={styles.modalCols}>
          <div className={styles.modalForm}>
            <label className={styles.field}>
              <span className="t-eyebrow">Name · lowercase_with_underscores</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="order_confirmation"
              />
              {cleanName && cleanName !== name && (
                <small>
                  Submits as <b>{cleanName}</b>
                </small>
              )}
            </label>

            <div className={styles.row}>
              <label className={styles.field}>
                <span className="t-eyebrow">Category</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as typeof category)}
                >
                  <option value="utility">Utility</option>
                  <option value="marketing">Marketing</option>
                  <option value="authentication">Authentication</option>
                </select>
              </label>
              <label className={styles.field}>
                <span className="t-eyebrow">Language</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as typeof language)}
                >
                  <option value="ms">Malay (ms)</option>
                  <option value="en">English (en)</option>
                </select>
              </label>
            </div>

            <div className={styles.field}>
              <span className="t-eyebrow">Header</span>
              <div className={styles.segmented} role="radiogroup" aria-label="Header type">
                <button
                  type="button"
                  data-active={headerFormat === "none"}
                  onClick={() => setHeaderFormat("none")}
                >
                  None
                </button>
                <button
                  type="button"
                  data-active={headerFormat === "image"}
                  onClick={() => setHeaderFormat("image")}
                >
                  Image
                </button>
              </div>
              {headerFormat === "image" && (
                <label className={styles.imagePick} data-filled={Boolean(headerFile)}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    onChange={(e) => pickImage(e.target.files?.[0] ?? null)}
                  />
                  {headerFile ? (
                    <>
                      <img src={headerUrl ?? undefined} alt="" />
                      <span>{headerFile.name} — tap to replace</span>
                    </>
                  ) : (
                    <span>
                      <b>Choose a sample image</b> (JPEG/PNG, ≤5MB) — Meta reviews it with the
                      template
                    </span>
                  )}
                </label>
              )}
            </div>

            <label className={styles.field}>
              <span className="t-eyebrow">
                Body · {body.trim().length}/1024 · {placeholders.length} variable
                {placeholders.length === 1 ? "" : "s"}
              </span>
              <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
            </label>

            {placeholders.length > 0 && (
              <div className={styles.field}>
                <span className="t-eyebrow">Variable mapping</span>
                {placeholders.map((p) => (
                  <input
                    key={p}
                    className={styles.varInput}
                    placeholder={`{{${p}}} → e.g. Customer name`}
                    value={labels[p] ?? ""}
                    onChange={(e) => setLabels({ ...labels, [p]: e.target.value })}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={styles.modalPreviewCol}>
            <span className="t-eyebrow">Live preview</span>
            <div className={`${styles.preview} ${styles.previewSticky}`}>
              <div className={styles.waBubble}>
                {headerFormat === "image" && (
                  <span className={styles.waHeaderImg}>
                    {headerUrl ? <img src={headerUrl} alt="" /> : <span>Header image</span>}
                  </span>
                )}
                <span className={styles.waText}>{previewText}</span>
                <span className={styles.waTime}>10:24</span>
              </div>
            </div>
          </div>
        </div>

        {(name || body) && errors.length > 0 && (
          <ul className={styles.errList}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}
        {serverErrors.length > 0 && (
          <ul className={styles.errList}>
            {serverErrors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        <div className={styles.modalActions}>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" disabled={!canSubmit} onClick={submit}>
            {submitting || create.isPending
              ? "Submitting…"
              : needsImage
                ? "Add a sample image"
                : "Submit to Meta"}
          </button>
        </div>
      </div>
    </div>
  );
}
